/*
 * Adia a montagem de um bloco até ele CHEGAR PERTO da viewport.
 *
 * Combinado com `React.lazy`, é isto que faz o adiamento valer: `lazy` sozinho já separa o
 * chunk, mas o `import()` dispara assim que o componente renderiza — o arquivo desce junto com
 * o resto. Com o gate, ele só desce quando o usuário se aproxima do bloco. No mobile a coluna
 * direita inteira está abaixo da dobra.
 *
 * Sem `IntersectionObserver` o conteúdo monta DIRETO: adiar sem saber quando soltar seria
 * esconder o bloco para sempre.
 */
import React, { useEffect, useRef, useState } from 'react';

/** Solta antes de entrar na tela, para o chunk chegar já rolado. */
const ROOT_MARGIN = '300px';

interface IDeferUntilVisibleProps {
    /** Um elemento só: é ele que assume o lugar do placeholder quando o bloco solta. */
    children: React.ReactElement;
    /** Ocupa o lugar enquanto o bloco não montou; sem ela a página salta ao soltar. */
    placeholderHeight?: number;
    className?: string;
}

const DeferUntilVisible: React.FC<IDeferUntilVisibleProps> = ({
    children,
    placeholderHeight,
    className
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(() => typeof IntersectionObserver === 'undefined');

    useEffect(() => {
        if (isVisible || !ref.current) return undefined;

        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            setIsVisible(true);
            observer.disconnect();
        }, { rootMargin: ROOT_MARGIN });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [isVisible]);

    if (isVisible) return children;

    return <div ref={ref} className={className} style={{ minHeight: placeholderHeight }} />;
};

export default DeferUntilVisible;
